import { useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import FloatingReserveButton from "@/components/FloatingReserveButton";
import { Button } from "@/components/ui/button";
import CanonicalTag from "@/components/CanonicalTag";

const MediterraneanADUDesignFremont = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Mediterranean ADU Design & Construction in Fremont",
    "description": "Professional Mediterranean ADU design and construction services in Fremont, CA. Custom accessory dwelling units with authentic Mediterranean architecture.",
    "provider": {
      "@type": "Organization",
      "name": "UDA ADU",
      "url": "https://udaadu.com"
    },
    "areaServed": "Fremont, CA",
    "serviceType": "Mediterranean ADU Design & Construction"
  };

  return (
    <>
      <CanonicalTag url="https://udaadu.com/mediterranean-adu-design-fremont" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <Navigation />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
            <button
              onClick={closeCalendlyModal}
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
            <iframe
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900">
        {/* Hero Section */}
        <section className="pt-24 pb-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Mediterranean ADU Design & Construction in Fremont
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Bring the warmth and elegance of Mediterranean architecture to your Fremont property with custom ADU design and construction services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={openCalendlyModal}
                size="lg"
                className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              >
                Get Free Consultation
              </Button>
              <Link to="/cost-estimate">
                <Button
                  variant="outline"
                  size="lg"
                  className="border-white text-white hover:bg-white hover:text-blue-900 px-8 py-4 text-lg font-semibold"
                >
                  Get Cost Estimate
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Why Choose Mediterranean Style */}
        <section className="py-16 px-4 bg-white/10 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Why Choose Mediterranean ADU Design in Fremont?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-4">Timeless Elegance</h3>
                <p className="text-gray-200">
                  Mediterranean design features warm colors, natural materials, and architectural details that create lasting beauty and value.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-4">Climate Perfect</h3>
                <p className="text-gray-200">
                  Designed for warm climates, Mediterranean ADUs are perfectly suited for Fremont's California weather patterns.
                </p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold text-white mb-4">Outdoor Living</h3>
                <p className="text-gray-200">
                  Emphasis on courtyards, patios, and seamless indoor-outdoor flow maximizes your property's living potential.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Mediterranean ADU Design Features
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Stucco Exteriors</h3>
                <p className="text-gray-200">
                  Authentic stucco finishes in warm earth tones create the classic Mediterranean aesthetic.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Clay Tile Roofs</h3>
                <p className="text-gray-200">
                  Traditional red clay tile roofing provides durability and authentic Mediterranean character.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Arched Openings</h3>
                <p className="text-gray-200">
                  Graceful arched windows and doorways add architectural interest and Mediterranean charm.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Wrought Iron Details</h3>
                <p className="text-gray-200">
                  Decorative wrought iron balconies, window grilles, and gates enhance the Mediterranean style.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Courtyard Design</h3>
                <p className="text-gray-200">
                  Private courtyards and outdoor living spaces extend your living area naturally.
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <h3 className="text-xl font-semibold text-white mb-3">Natural Materials</h3>
                <p className="text-gray-200">
                  Stone, wood, and terra cotta elements create authentic Mediterranean warmth and texture.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Fremont Local Considerations */}
        <section className="py-16 px-4 bg-white/10 backdrop-blur-sm">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Fremont Mediterranean ADU Considerations
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-2xl font-semibold text-white mb-4">Local Regulations</h3>
                <ul className="space-y-3 text-gray-200">
                  <li>• ADU size up to 1,200 sq ft permitted</li>
                  <li>• 4-foot minimum side and rear setbacks</li>
                  <li>• Height limit of 16 feet for detached ADUs</li>
                  <li>• Owner-occupancy requirement waived until 2025</li>
                </ul>
              </div>
              <div>
                <h3 className="text-2xl font-semibold text-white mb-4">Design Integration</h3>
                <ul className="space-y-3 text-gray-200">
                  <li>• Complement existing neighborhood architecture</li>
                  <li>• Consider earthquake resistance requirements</li>
                  <li>• Energy efficiency standards compliance</li>
                  <li>• Landscape integration with Mediterranean plants</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Cost Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
              Mediterranean ADU Investment in Fremont
            </h2>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              Mediterranean ADUs typically range from $200,000 to $400,000 depending on size, finishes, and complexity. 
              The investment in authentic Mediterranean design often provides excellent long-term value and rental potential.
            </p>
            <Link to="/cost-estimate">
              <Button
                size="lg"
                className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
              >
                Get Detailed Cost Estimate
              </Button>
            </Link>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 bg-white/10 backdrop-blur-sm">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
              Ready to Build Your Mediterranean ADU in Fremont?
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Let's discuss your Mediterranean ADU project and bring Mediterranean elegance to your Fremont property.
            </p>
            <Button
              onClick={openCalendlyModal}
              size="lg"
              className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-4 text-lg font-semibold"
            >
              Schedule Your Free Consultation
            </Button>
          </div>
        </section>

        {/* Internal Links */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-8">
              Explore More ADU Designs in Fremont
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/contemporary-adu-design-fremont" className="block p-6 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors">
                <h3 className="text-lg font-semibold text-white mb-2">Contemporary ADU Design Fremont</h3>
                <p className="text-gray-200">Modern, sleek designs for today's lifestyle</p>
              </Link>
              <Link to="/craftsman-adu-design-fremont" className="block p-6 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors">
                <h3 className="text-lg font-semibold text-white mb-2">Craftsman ADU Design Fremont</h3>
                <p className="text-gray-200">Traditional craftsmanship with modern amenities</p>
              </Link>
              <Link to="/modern-farmhouse-adu-design-fremont" className="block p-6 bg-white/10 backdrop-blur-sm rounded-lg hover:bg-white/20 transition-colors">
                <h3 className="text-lg font-semibold text-white mb-2">Modern Farmhouse ADU Design Fremont</h3>
                <p className="text-gray-200">Rustic charm meets contemporary comfort</p>
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />
      <FloatingADUWizard />
      <FloatingReserveButton />
    </>
  );
};

export default MediterraneanADUDesignFremont;